
import { DashboardController } from "@/app/ui/controllers/DashboardController";
import { Options, prop, Vue } from "vue-class-component";
import { CourierDailyDepositRequest } from "@/data/payload/api/DashboardRequests";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import parsingErrorResponse from "@/app/infrastructures/misc/common-library/ParsingErrorResponse";
import {
  ErrorMessageEntities,
  ModalMessageEntities
} from "@/domain/entities/MainApp";
import { CodReconcileController } from "@/app/ui/controllers/CodReconcileController";
import {
  CourierDetail,
  CourierDetailApiRequest,
  CourierHistory,
  CourierNotesPhoto,
  CourierSummary,
  DepositAmount,
  TaskSetllementCourier
} from "@/domain/entities/CodReconcile";
import whatsapp from "@/app/infrastructures/misc/common-library/Whatsapp";
import { CourierPartnerCodDashboard } from "@/domain/entities/Dashboard";
import {
  formatByCountry,
  formatInputByCountry,
  formatPriceWithoutCurrency,
  resolveCountry
} from "@/app/infrastructures/misc/Utils";
import { getHubData } from "@/app/infrastructures/misc/Cookies";
import SectionUploadPhotoCodReconcile from "../section-upload-photo-cod-reconcile.vue";
import Notes from "../notes.vue";
import {
  GetCourierNotes,
  SubmitCourierNotes,
  SubmitCourierNotesPhotos,
  TaskSettlementCourierParams
} from "@/data/payload/api/CodReconcileApiRequest";
import { AccountController } from "@/app/ui/controllers/AccountController";
import ImagePreviewReconcile from "../image-preview-reconcile.vue";
import HistoryTable from "../history-table.vue";
import SttDetailModal from "@/app/ui/views/cod-delivery-v2/components/popup-detail-stt.vue";
import SummaryCollapsible from "../summary-collapsible.vue";
import CardDeposit from "./card-deposit.vue";

class Props {
  courier = prop<any>({
    default: null
  });
  type = prop<string>({
    default: "courier",
    type: String
  });
  filterDate = prop<string>({
    default: "",
    type: String
  });
}

@Options({
  components: {
    Notes,
    SectionUploadPhotoCodReconcile,
    ImagePreviewReconcile,
    HistoryTable,
    SttDetailModal,
    SummaryCollapsible,
    CardDeposit
  }
})
export default class PopupSetorHarianKurir extends Vue.with(Props) {
  sttNumber = "";
  isSearchSTT = false;
  isEmptySTT = false;
  expandSttPending = false;
  refs = null as any;
  courierNotes = "";
  courierNotePhotos: CourierNotesPhoto[] = [];
  courierNotesLoading = false;
  courierNotesPhotosLoading = false;
  previewCourierNotesPhoto = false;
  previewCourierNotesPhotoIndex = 0;
  courierHistory: CourierHistory[] = [];

  showModalDetailStt = false;
  isShowModalPreviewImage = false;

  mounted() {
    this.onGetAllData();
    this.refs = this.$refs;
  }

  onGetAllData() {
    if (!this.courier?.courierId) return;

    this.getTaskSettlementCourier();
    this.getListSttTable();
    this.getListSttCollapse();
    this.getCourierSummaryDetail();
    this.getCourierNotes();
    this.getCourierHistory();
  }

  get isDisabled() {
    return !this.dataSelected.length;
  }

  get courierNotePhotosUrl() {
    return this.courierNotePhotos.map((photo) => photo.url);
  }
  set courierNotePhotosUrl(payload: string[]) {
    this.courierNotePhotos = payload.map((url) => {
      const existingData = this.courierNotePhotos.find(
        (photo) => photo.url === url
      );

      if (existingData) return existingData;
      return new CourierNotesPhoto({
        url,
        uploadedAt: new Date().toISOString(),
        uploadedBy: AccountController.accountData.account_id,
        uploadedByType: AccountController.accountData.account_type_detail.type
      });
    });
  }

  form = {
    deposit: ""
  };
  maxDate = new Date();

  get prefixCurrency() {
    return resolveCountry() === "MY" ? "RM" : "Rp";
  }

  formatMoney(value: string) {
    return formatInputByCountry(value, { type: "money" });
  }
  formatNumber(value: string) {
    return value.replace(/[\D]+/g, "");
  }
  formatPriceRp(value: number) {
    return formatByCountry(value, { type: "money", prefix: this.prefixCurrency });
  }
  formatPriceWithoutCurrency(value: number) {
    return formatPriceWithoutCurrency(value);
  }
  async postCourierDailyDeposit() {
    try {
      MainAppController.showLoading();
      const response = await DashboardController.postCourierDailyDepositV3({
        type: this.type,
        payload: new CourierDailyDepositRequest({
          driverPhone: this.courier.courierPhone.replaceAll("+", ""),
          picPhone: this.courier.courierPhone.replaceAll("+", ""),
          filterDate: this.$moment(this.filterDate).format("YYYY-MM-DD"),
          totalAmount: 0,
          stt: this.dataSelected
        })
      });

      if (response.success) {
        MainAppController.showMessageModal(
          new ModalMessageEntities({
            title: this.$t("codReconcile.dailyDeposit.successTitle"),
            message: `${this.dataSelected.length} STT berhasil disetor.`,
            textSuccess: "OK",
            onSubmit: () => {
              MainAppController.closeMessageModal();
              this.onGetAllData();
            }
          })
        );
      }
    } catch (error) {
      const errorMessage = (error as any)?.response?.data?.message?.id;
      MainAppController.showErrorMessage(
        errorMessage
          ? new ErrorMessageEntities({
              type: "client-error",
              title: this.$t("codReconcile.dailyDeposit.errorTitle"),
              message: errorMessage,
              buttonSuccessText: "OK"
            })
          : parsingErrorResponse(
              error,
              this.$t("codReconcile.dailyDeposit.errorMessage"),
              this.postCourierDailyDeposit
            )
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  courierSummary = new CourierSummary();
  get courierSummaryCount() {
    const dex = new DepositAmount(
      this.courierSummary.sttDeposit.find(
        (e: DepositAmount) => e.status.toLowerCase() === "dex"
      )
    );

    const sttDex = dex.pending;
    const sttDexPieces = dex.pendingPieces;

    const codrej = new DepositAmount(
      this.courierSummary.sttDeposit.find(
        (e: DepositAmount) => e.status.toLowerCase() === "codrej"
      )
    );
    const sttCodrej = codrej.pending;
    const sttCodrejPieces = codrej.pendingPieces;

    return new CourierPartnerCodDashboard({
      totalSttDex: sttDex,
      totalSttDexPieces: sttDexPieces,
      totalSttCodrej: sttCodrej,
      totalSttCodrejPieces: sttCodrejPieces,
      totalStt: sttDex + sttCodrej,
      totalSttPieces: sttDexPieces + sttCodrejPieces
    });
  }

  get listCourierSummary() {
    return [
      {
        sttStatus: "DEX",
        sttAmount: this.courierSummaryCount.totalSttDex,
        koliAmount: this.courierSummaryCount.totalSttDexPieces
      },
      {
        sttStatus: "CODREJ",
        sttAmount: this.courierSummaryCount.totalSttCodrej,
        koliAmount: this.courierSummaryCount.totalSttCodrejPieces
      }
    ];
  }

  async getCourierSummaryDetail(date?: Date) {
    try {
      MainAppController.showLoading();
      const res: CourierSummary = await CodReconcileController.getCourierSummaryDetailV2(
        {
          params: new CourierDetailApiRequest({
            type: "DRIVER",
            filterDate: this.$moment(date || this.filterDate).format(
              "YYYY-MM-DD"
            ),
            driverPhone: this.courier.courierPhone.replace(/^\+/, ""),
            hubId: getHubData()?.hubId || 0
          }),
          version: "v3"
        }
      );

      this.courierSummary = res;
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          error,
          "Gagal Mendapatkan Summary!",
          this.getCourierSummaryDetail
        )
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  async getListSttTable() {
    await CodReconcileController.getCourierDetail({
      params: new CourierDetailApiRequest({
        type: this.type,
        filterDate: this.$moment(this.filterDate).format("YYYY-MM-DD"),
        driverPhone: this.courier.courierPhone,
        picPhone: this.courier.courierPhone,
        status: "failed"
      }),
      version: "v3"
    });
    this.isSearchSTT = false;
    this.isEmptySTT = false;
  }

  listSttCollapse: CourierDetail[] = [];
  async getListSttCollapse() {
    try {
      MainAppController.showLoading();
      const res: CourierDetail[] = await CodReconcileController.getCourierDetailV2(
        {
          params: new CourierDetailApiRequest({
            type: this.type,
            filterDate: this.$moment(this.filterDate).format("YYYY-MM-DD"),
            driverPhone: this.courier.courierPhone,
            status: "success"
          }),
          version: "v3"
        }
      );
      this.listSttCollapse = res.filter(
        (e: CourierDetail) =>
          e.sttStatus === "POD" &&
          e.sttCollectionStatus.toLowerCase() === "belum setor"
      );
    } catch (error) {
      const message = (error as any)?.response?.data?.message;
      MainAppController.showErrorMessage(
        typeof message === "string"
          ? new ErrorMessageEntities({
              title: "Gagal Mengambil Detail Kurir",
              message
            })
          : parsingErrorResponse(error)
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  taskSettlementCourier: TaskSetllementCourier[] = [];
  async getTaskSettlementCourier() {
    try {
      MainAppController.showLoading();
      this.taskSettlementCourier = await CodReconcileController.getTaskSettlementCourier(
        new TaskSettlementCourierParams({
          id: this.courier.courierId,
          date: this.$moment(this.filterDate).format("YYYY-MM-DD"),
          reconcileStatus: "HIDE_ALL",
          reconcileFor: "PACKAGE"
        })
      );
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          error,
          "Gagal mengambil data setllement",
          this.getTaskSettlementCourier
        )
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  async getCourierNotes() {
    const resp = await CodReconcileController.getCourierNotes(
      new GetCourierNotes({
        reconcileFor: "PACKAGE",
        courierId: Number(this.courier.courierId),
        date: this.$moment(this.filterDate).format("YYYY-MM-DD")
      })
    );

    this.courierNotePhotos = resp.photos;
    this.courierNotes = resp.note;
    if (!this.courierNotePhotos.length) {
      this.courierNotePhotosUrl = ["", "", "", ""];
    } else {
      const remainingSlot = 4 - this.courierNotePhotos.length;
      for (let i = 0; i < remainingSlot; i++) {
        this.courierNotePhotosUrl.push("");
      }
    }
  }

  async getCourierHistory() {
    const resp = await CodReconcileController.getCourierHistory(
      new GetCourierNotes({
        reconcileFor: "PACKAGE",
        courierId:
          this.courier.courierId || this.detailDashbordCourier.courierId,
        date: this.$moment(this.filterDate).format("YYYY-MM-DD") as string
      })
    );

    this.courierHistory = resp;
  }

  async onSubmitCourierNotesPhoto(payload: { url: string; index: number }) {
    try {
      this.courierNotePhotosUrl[payload.index] = payload.url;
      MainAppController.showLoading();
      const requestPayload = new SubmitCourierNotesPhotos({
        reconcileFor: "PACKAGE",
        courierId:
          this.courier.courierId || this.detailDashbordCourier.courierId,
        date: this.$moment(this.filterDate).format("YYYY-MM-DD"),
        photos: this.courierNotePhotos.filter((photo) => photo.url)
      });
      await CodReconcileController.uploadCourierNotesPhotos(requestPayload);
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          error,
          "Gagal mengupload foto catatan kurir",
          this.onSubmitCourierNotesPhoto
        )
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  async onSubmitCourierNotes() {
    try {
      this.courierNotesLoading = true;
      const payload = new SubmitCourierNotes({
        reconcileFor: "PACKAGE",
        courierId:
          this.courier.courierId || this.detailDashbordCourier.courierId,
        date: this.$moment(this.filterDate).format("YYYY-MM-DD"),
        note: this.courierNotes
      });
      await CodReconcileController.submitCourierNotes(payload);
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          error,
          "Gagal menyimpan catatan kurir",
          this.onSubmitCourierNotes
        )
      );
    } finally {
      this.courierNotesLoading = false;
    }
  }

  dataSelected: string[] = [];
  onSelectData(data: CourierDetail[]) {
    this.dataSelected = data.map((e: CourierDetail) => e.sttNo);
  }

  isScanning = false;
  lastScanChar = "";

  onScanSttNumber(val: string, self = this) {
    if (!val) return;
    MainAppController.setSnackbar(false);
    if (!self.sttNumber) {
      return;
    }
    const splitVal = val.split("#")[0];
    const refs: any = self.refs.detailCourierTable;
    const selectedValue = self.detailCourier.find((item: any) => {
      return item.sttNo === self.sttNumber;
    });

    self.sttNumber = splitVal;
    self.isSearchSTT = true;
    if (selectedValue) {
      if (!self.dataSelected.includes(splitVal)) {
        refs.selectedCheckbox.push(selectedValue);
        MainAppController.setSnackbarMessage("Berhasil Scan");
        MainAppController.setSnackbarPositivity(true);
        MainAppController.setSnackbarTimeout(1500);
        MainAppController.setSnackbar(true);
      } else {
        MainAppController.setSnackbarMessage("STT sudah dipilih");
        MainAppController.setSnackbarPositivity(false);
        MainAppController.setSnackbarTimeout(1500);
        MainAppController.setSnackbar(true);
      }
      self.isEmptySTT = false;
    } else {
      self.isEmptySTT = true;
    }
    if (!self.isEmptySTT) {
      self.sttNumber = "";
    }
    this.isScanning = false;
  }

  onFocusStt(event: any) {
    const val = event.target.value?.toUpperCase();
    const inputType = event.inputType;
    const inputEl = document.getElementById(
      "cod-reconcile-scan"
    ) as HTMLInputElement;
    if (!this.isScanning) {
      this.sttNumber = "";
      inputEl.value = "";
      this.lastScanChar = val[val.length - 1] || "";
      this.isScanning = true;
      if (this.isEmptySTT) {
        if (inputType === "deleteContentBackward") {
          this.sttNumber = "";
        } else {
          if (inputType === "insertFromPaste") {
            let splitVal = val.split(" ");
            splitVal = splitVal[splitVal.length - 1];
            this.sttNumber = splitVal?.toUpperCase();
          } else {
            this.sttNumber = this.lastScanChar?.toUpperCase();
          }
        }
      } else {
        this.sttNumber = val?.toUpperCase();
      }
    } else {
      this.sttNumber = val?.toUpperCase();
    }
    if (this.isEmptySTT) {
      this.isEmptySTT = false;
    }
  }

  // table
  get detailCourier() {
    return CodReconcileController.courierDetailList.data;
  }

  get detailDashbordCourier() {
    return CodReconcileController.dashboardDeliveryCourierDetail;
  }

  get isLoading() {
    return CodReconcileController.isLoading;
  }

  get isEmptyList(): boolean {
    return !this.detailCourier.length || this.isEmptySTT;
  }

  get emptyIcon(): string {
    return this.isSearchSTT ? "noData_filter" : "noData_empty";
  }

  get emptyTitle(): string {
    return this.isSearchSTT
      ? this.$t("codReconcile.dailyDeposit.emptyTitleSttNotFound")
      : "";
  }

  get emptyMessage(): string {
    return this.isSearchSTT
      ? this.$t("codReconcile.dailyDeposit.emptyMessageSttNotFound")
      : this.$t("codReconcile.dailyDeposit.emptyMessageNoStt");
  }

  get columns() {
    return [
      {
        name: this.$t("codReconcile.list.no"),
        styleHead: "w-12 text-left whitespace-no-wrap",
        render: (item: CourierDetail, index: any) => {
          return `<div class="overflow-ellipsis text-left text-14px">${index +
            1}</div>`;
        }
      },
      {
        name: this.$t("codReconcile.dailyDeposit.noStt"),
        styleHead: "w-auto text-left whitespace-nowrap",
        render: (item: CourierDetail) => {
          return `<div class="text-left text-14px">${item.sttNo}</div>`;
        }
      },
      {
        name: this.$t("codReconcile.detail.statusSetoranLast"),
        styleHead: "w-2/12 text-left whitespace-nowrap",
        render: (item: CourierDetail) => {
          return `<div class="overflow-ellipsis text-left break-all text-14px">${item.sttStatus}</div>`;
        }
      },
      {
        name: this.$t("codReconcile.detail.alasan"),
        styleHead: "w-4/12 text-left whitespace-nowrap",
        render: (item: CourierDetail) => {
          return `<div class="overflow-ellipsis text-left break-all text-14px">${item.sttReason}</div>`;
        }
      },
      {
        name: this.$t("codReconcile.detail.namaPenerimaTab"),
        key: "actionable_column",
        styleHead: "w-3/12 text-left whitespace-nowrap",
        render: (item: CourierDetail) => {
          return `<div class="overflow-ellipsis text-left break-all text-14px mr-4">${item.sttRecipientName}</div>
            <div class="text-black-lp-300 font-semibold text-14px mr-4">${item.sttRecipientPhone}</div>`;
        },
        showButton: () => true,
        styleCustom: "items-end",
        icon: "whatsapp",
        iconStyle: "pt-4",
        disableButtonValue: () => false,
        onClickValue: this.onCLickWhatsapp
      }
    ];
  }

  // whatsapp
  onCLickWhatsapp(item: CourierDetail) {
    whatsapp(item.sttRecipientPhone);
  }

  get sttList() {
    return CodReconcileController.courierDetailStt;
  }

  get previewImageDetalStt() {
    return CodReconcileController.previewImageDetailStt.map(
      (photo: any) => photo.url
    );
  }

  closeDetailStt() {
    this.showModalDetailStt = false;
  }

  showModalStt() {
    this.showModalDetailStt = true;
  }

  showModalPreviewImage() {
    this.isShowModalPreviewImage = true;
  }
}
